import React, { useRef, useState } from 'react'
import { Upload, Trash2, RefreshCw, CheckCircle } from 'lucide-react'
import { externalHoldingsAPI } from '../services/api'
import toast from 'react-hot-toast'

const JsonUploadPanel = ({ accountName, uploadedAt, onUploaded, onCleared }) => {
  const inputRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [dragging, setDragging] = useState(false)

  const processFile = async (file) => {
    if (!file) return
    if (!file.name.endsWith('.json')) {
      toast.error('Please upload a .json file')
      return
    }
    setLoading(true)
    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      const res = await externalHoldingsAPI.upload(accountName, parsed)
      toast.success(`Uploaded ${res.data.summary.holdingsCount} holdings`)
      onUploaded && onUploaded(res.data)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed — check JSON format')
    } finally {
      setLoading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleFileChange = (e) => processFile(e.target.files[0])

  const handleDrop = (e) => {
    e.preventDefault()
    setDragging(false)
    processFile(e.dataTransfer.files[0])
  }

  const handleClear = async () => {
    if (!window.confirm('Clear all holdings for this account?')) return
    setLoading(true)
    try {
      await externalHoldingsAPI.clear(accountName)
      toast.success('Holdings cleared')
      onCleared && onCleared()
    } catch {
      toast.error('Failed to clear holdings')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {uploadedAt ? (
            <>
              <CheckCircle className="w-4 h-4 text-success-500" />
              <span>Last uploaded: {new Date(uploadedAt).toLocaleString('en-IN')}</span>
            </>
          ) : (
            <span className="text-gray-400">No holdings uploaded yet</span>
          )}
        </div>
        {uploadedAt && (
          <button
            onClick={handleClear}
            disabled={loading}
            className="flex items-center gap-1 text-xs text-danger-600 hover:text-danger-700"
          >
            <Trash2 className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>

      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50/30'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".json"
          className="hidden"
          onChange={handleFileChange}
        />
        {loading ? (
          <div className="flex flex-col items-center gap-2 text-primary-600">
            <RefreshCw className="w-6 h-6 animate-spin" />
            <span className="text-sm">Processing…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Upload className="w-6 h-6" />
            <span className="text-sm font-medium">Drop JSON or click to upload</span>
            <span className="text-xs text-gray-400">Kite or AngelOne holdings export</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default JsonUploadPanel
